import { Component, computed, inject, signal } from "@angular/core";
import { SaveActionButtonBase } from "../_save-button-base/save-button-base";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { combineLatest, exhaustMap, take, takeUntil, timer } from "rxjs";
import { Application, DataContainer } from "app/shared/model";
import { DocGenService } from "app/modules/forms/application-editor/control-list/controls/doc-gen/doc-gen.service";
import { DocumentGenerationResult } from "app/shared/services/model/doc-gen-service.model";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";

@Component({
    selector: 'save-and-print-button',
    templateUrl: './save-and-print-button.component.html',
    styleUrl: './save-and-print-button.component.scss',
    imports: [MatButtonModule, MatIconModule, MatProgressSpinnerModule],
})
export class SaveAndPrintButtonComponent extends SaveActionButtonBase
{
    private readonly _docGenService = inject(DocGenService);

    generating = signal(false);

    buttonText = computed(() => {
        return this.saving()
            ? 'Saving your application...'
            : this.generating()
                ? 'Generating your PDF...'
                : 'Save and Print your Application';
    });

    constructor() {
        super();
        this.loadConfig('saveAndPrintConfig.json');
    }

    saveAndPrint() {
        this.saveApplication()
            .subscribe({
                next: (result) => {
                    this.generateDocument();
                }
            });
    }

    generateDocument() {
        if (!this.currentConfig || this.generating()) {
            return;
        }

        this.generating.set(true);

        // application: Application, applicationData: DataContainer
        combineLatest({
            application: this._formService.application$,
            applicationData: this._formService.applicationData$
        })
            .pipe(take(1))
            .subscribe({
                next: (value: { application: Application, applicationData: DataContainer }) => {
                    this.generateAndListen(value.application, value.applicationData);
                }
            });
    }

    generateAndListen(application: Application, applicationData: DataContainer)
    {
        if (!this.currentConfig) {
            return;
        }

        // generate and download...
        const subscription = timer(0, 1000) // every second
            .pipe(
                // make sure we cancel this if the user closes the form.
                takeUntil(this._unsubscribeAll),
                // echaustMap: Ignore the timer signal if there's an operation in progress...
                exhaustMap(() => {
                    return this._docGenService.generateDocument(
                        application.id,
                        this.currentConfig.data['controlName'],
                        applicationData,
                        '',
                    );
                }),
            )
            .subscribe({
                next: (response: DocumentGenerationResult) => {
                    if (response.ready) {
                        // this.updateDocGenLink(res.url);
                        subscription.unsubscribe();
                        this.document.defaultView?.open(response.url, '_blank');
                        this.generating.set(false);
                    } else if (!response.retry) {
                        subscription.unsubscribe();
                        let err = response.status || 'The requested document is unavailable at the moment. Please try again later.';
                        this._alertService.danger(err);
                        this.generating.set(false);
                    }

                    // otherwise, keep generating() set as we're still waiting...
                },
                error: (error) => {
                    this.generating.set(false);
                    subscription.unsubscribe();
                    let err = !!error?.message
                        ? error.message
                        : 'Error occurred during document generation.';

                    this._alertService.danger(err);
                },
            });
    }
}
